/* BlueCollarCareerDetail.jsx — one preview per skilled-service career.
   Reads /data/skilled_service_careers.json (173 careers · with quizzes
   + AI/Automation outlook). UI matches /previews + /pathways/switch + /explore/ai
   pattern: cream bg, big teal gradient hero, sticky pill nav, bordered cards. */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Quote, Briefcase, Wrench, BookOpen, TrendingUp,
  Building2, Bot, ShieldAlert, Smartphone, ChevronLeft, ChevronRight,
  Brain, Heart, DollarSign, GraduationCap, Award,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import ContentLockedCard from '../components/ContentLockedCard';
import PreviewUnlockOverlay from '../components/PreviewUnlockOverlay';
import { usePreviewQuota } from '../hooks/usePreviewQuota';
import { loadContent, isLocked } from '../lib/contentLoader';
import {
  Section, InfoCard, ChipList, PillNav, QuizYesNoMcq,
} from '../components/preview-ui/PreviewPrimitives';

const SECTIONS = [
  { id: 'respect',     label: 'Why It Matters',      icon: Heart },
  { id: 'daily',       label: 'Daily Work',          icon: Briefcase },
  { id: 'skills',      label: 'Skills',              icon: Wrench },
  { id: 'tools',       label: 'Tools & Products',    icon: BookOpen },
  { id: 'training',    label: 'Training',            icon: GraduationCap },
  { id: 'earnings',    label: 'Earnings',            icon: DollarSign },
  { id: 'growth',      label: 'Growth Path',         icon: TrendingUp },
  { id: 'business',    label: 'Own Business Path',   icon: Building2 },
  { id: 'ai-outlook',  label: 'AI & Automation',     icon: Bot },
  { id: 'platform',    label: 'Start on Platform',   icon: Smartphone },
  { id: 'safety',      label: 'Safety & Trust',      icon: ShieldAlert },
  { id: 'quiz',        label: 'Quiz',                icon: Brain },
];

const BlueCollarCareerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  usePreviewQuota(`bluecollar-${id}`, 'blue-collar');

  useEffect(() => {
    loadContent('skilled_service_careers.json')
      .then((d) => {
        if (isLocked(d)) { setLocked(true); setLoading(false); return; }
        if (d) setPkg(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  const { career, adjacent } = useMemo(() => {
    if (!pkg?.careers) return { career: null, adjacent: { prev: null, next: null } };
    const sorted = [...pkg.careers].sort((a, b) => (a.id || 0) - (b.id || 0));
    const idx = sorted.findIndex((c) => String(c.id) === String(id));
    return {
      career: idx >= 0 ? sorted[idx] : null,
      adjacent: {
        prev: idx > 0 ? sorted[idx - 1] : null,
        next: idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null,
      },
    };
  }, [pkg, id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[70vh]" data-testid="bc-loading">
          <Loader2 className="w-10 h-10 text-teal-600 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (locked) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="pt-32 px-4"><ContentLockedCard /></div>
        <Footer />
      </div>
    );
  }

  if (!career) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md" data-testid="bc-not-found">
            <p className="text-2xl font-bold text-gray-900 mb-2">Career not found</p>
            <p className="text-gray-600 mb-6">We couldn&apos;t find career #{id}.</p>
            <Button asChild>
              <Link to="/explore/careers-ts-ap">Back to Careers</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <PreviewUnlockOverlay
      pathKey="banking_bluecollar"
      previewId={String(career.id)}
      itemName={career.title}
      itemType="blue"
    >
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 md:pt-36 pb-12 px-4 md:px-6 bg-gradient-to-br from-amber-500 via-orange-500 to-amber-700 text-white rounded-b-[2.5rem] overflow-hidden">
        <div className="absolute -top-12 right-8 w-48 h-48 rounded-full bg-white/10" aria-hidden="true" />
        <div className="absolute bottom-0 -left-10 w-36 h-36 rounded-full bg-white/10" aria-hidden="true" />
        <div className="max-w-4xl mx-auto relative">
          <Link
            to="/explore/careers-ts-ap"
            data-testid="bc-back"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-5 transition-colors font-semibold"
          >
            <ArrowLeft size={14} /> Back to Careers
          </Link>
          <div className="flex flex-wrap items-center gap-1.5 mb-5 text-[10px] font-semibold tracking-wide uppercase">
            <span className="bg-white/20 text-white/95 px-2.5 py-0.5 rounded-full tabular-nums">
              #{String(career.id).padStart(3, '0')}
            </span>
            {(() => {
              const seen = new Set();
              const chips = [career.category, career.platform && `via ${career.platform}`]
                .filter(Boolean)
                .filter((c) => {
                  const k = String(c).trim().toLowerCase();
                  if (!k || seen.has(k)) return false;
                  seen.add(k);
                  return true;
                });
              return chips.map((c, i) => (
                <span key={i} className="bg-white/15 text-white/90 px-2.5 py-0.5 rounded-full">{c}</span>
              ));
            })()}
          </div>
          <h1
            data-testid="bc-title"
            className="text-3xl md:text-5xl font-bold leading-[1.15] tracking-tight mb-3"
          >
            {career.title}
          </h1>
          {career.headline && career.headline !== career.title && (
            <p className="text-base md:text-lg text-white/85 font-medium mb-6">{career.headline}</p>
          )}
          {career.description && (
            <blockquote className="relative bg-white/10 backdrop-blur border border-white/20 rounded-3xl px-6 py-6 md:px-8 md:py-8">
              <Quote size={26} className="text-amber-300 mb-3" />
              <p className="text-lg md:text-2xl text-white leading-[1.5] italic">{career.description}</p>
            </blockquote>
          )}
        </div>
      </section>

      <PillNav sections={SECTIONS} testidPrefix="bc-nav" hoverTone="amber" />

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* Respect / why it matters */}
        {career.respectMessage && (
          <Section id="respect" icon={Heart} title="Why This Work Matters" accent="bluecollar_primary">
            <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <Award size={22} className="text-rose-600 shrink-0 mt-0.5" />
                <p className="text-[15px] md:text-base text-gray-800 leading-relaxed font-medium">{career.respectMessage}</p>
              </div>
            </div>
          </Section>
        )}

        {/* Daily work */}
        {career.dailyWork && (
          <Section id="daily" icon={Briefcase} title="Daily Work" accent="bluecollar_primary">
            <p>{career.dailyWork}</p>
          </Section>
        )}

        {/* Skills */}
        {career.skillsToBuild?.length > 0 && (
          <Section id="skills" icon={Wrench} title="Skills to Build" accent="bluecollar_primary">
            <ChipList items={career.skillsToBuild} tone="teal" />
          </Section>
        )}

        {/* Tools / products */}
        {career.toolsAndProducts?.length > 0 && (
          <Section id="tools" icon={BookOpen} title="Tools & Products" accent="bluecollar_primary">
            <ChipList items={career.toolsAndProducts} tone="blue" />
          </Section>
        )}

        {/* Training */}
        {career.trainingAndCertification && (
          <Section id="training" icon={GraduationCap} title="Training & Certification" accent="bluecollar_primary">
            <p>{career.trainingAndCertification}</p>
          </Section>
        )}

        {/* Earnings */}
        {career.earningPatternIndia && (
          <Section id="earnings" icon={DollarSign} title="Earning Pattern (India)" accent="emerald">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <DollarSign size={22} className="text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-[15px] text-gray-800 leading-relaxed">{career.earningPatternIndia}</p>
              </div>
            </div>
          </Section>
        )}

        {/* Growth path */}
        {career.growthPath && (
          <Section id="growth" icon={TrendingUp} title="Growth Path" accent="bluecollar_primary">
            <p>{career.growthPath}</p>
          </Section>
        )}

        {/* Independence / business */}
        {career.independenceAndBusinessPath && (
          <Section id="business" icon={Building2} title="Independence & Own-Business Path" accent="bluecollar_primary">
            <InfoCard label="Build your own thing" tone="cream">{career.independenceAndBusinessPath}</InfoCard>
          </Section>
        )}

        {/* AI & automation outlook */}
        {career.aiAndAutomationOutlook && (
          <Section id="ai-outlook" icon={Bot} title="AI & Automation Outlook" accent="bluecollar_primary">
            <InfoCard label="What stays human" tone="violet">{career.aiAndAutomationOutlook}</InfoCard>
          </Section>
        )}

        {/* How to start on platform */}
        {career.howToStartOnPlatform && (
          <Section id="platform" icon={Smartphone} title={`How to Start on ${career.platform || 'the Platform'}`} accent="bluecollar_primary">
            <p>{career.howToStartOnPlatform}</p>
          </Section>
        )}

        {/* Safety & trust */}
        {career.safetyTrustAndProfessionalism && (
          <Section id="safety" icon={ShieldAlert} title="Safety, Trust & Professionalism" accent="rose">
            <InfoCard label="Non-negotiables" tone="rose">{career.safetyTrustAndProfessionalism}</InfoCard>
          </Section>
        )}

        {/* Quiz */}
        {career.quiz && (
          <Section id="quiz" icon={Brain} title="Career Fit Quiz — Try It" accent="bluecollar_primary">
            <QuizYesNoMcq quiz={career.quiz} testidPrefix="bc-quiz" />
          </Section>
        )}

        {/* Prev / Next */}
        <nav className="mt-6 grid sm:grid-cols-2 gap-3" aria-label="Adjacent careers">
          {adjacent.prev ? (
            <button
              type="button"
              data-testid="bc-prev"
              onClick={() => navigate(`/explore/careers-ts-ap/blue-collar/${adjacent.prev.id}`)}
              className="text-left p-4 md:p-5 rounded-2xl border-2 border-gray-200 bg-white hover:border-amber-500 hover:shadow-md transition-all group"
            >
              <p className="text-[11px] font-bold tracking-widest text-gray-500 uppercase mb-1 flex items-center gap-1">
                <ChevronLeft size={14} /> Previous
              </p>
              <p className="font-bold text-gray-900 truncate group-hover:text-amber-700">{adjacent.prev.title}</p>
            </button>
          ) : <span />}
          {adjacent.next ? (
            <button
              type="button"
              data-testid="bc-next"
              onClick={() => navigate(`/explore/careers-ts-ap/blue-collar/${adjacent.next.id}`)}
              className="text-right p-4 md:p-5 rounded-2xl border-2 border-gray-200 bg-white hover:border-amber-500 hover:shadow-md transition-all group sm:col-start-2"
            >
              <p className="text-[11px] font-bold tracking-widest text-gray-500 uppercase mb-1 flex items-center justify-end gap-1">
                Next <ChevronRight size={14} />
              </p>
              <p className="font-bold text-gray-900 truncate group-hover:text-amber-700">{adjacent.next.title}</p>
            </button>
          ) : null}
        </nav>

      </main>

      <Footer />
    </div>
    </PreviewUnlockOverlay>
  );
};

export default BlueCollarCareerDetail;
